// @ts-nocheck
import { cloneDeep } from 'lodash';
import { isDigitalCampaign, isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { statsFilters } from 'sonata-static-storage/lib/samples/statsPreferences.samples';

import {
  campaignsHaveContextualBidding,
  campaignsHaveDCO,
  campaignsHaveDigitalConversions,
  getVariableFilterPermission
} from '@/helpers/reporting.helpers';

import type { GeoDictionaryTagsConfig } from 'mercator-cartography-api-client/lib/interfaces/geoDictionary.types';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

const COMMON_TABLE_VARIABLES = [
  'bidMultiplier',
  'mediaFee',
  'mloTotalEvents',
  'mloLogSum',
  'total_viewers',
  'unique_users',
  'winPrice',
  'winPriceUser'
];

type TableType = 'footfallAttribution' | 'stats' | 'viewability';

const TABLE_VARIABLES_BY_TYPE: Record<TableType, string[]> = {
  footfallAttribution: [],
  stats: ['type'],
  viewability: []
};

function getUniqueKeys<T>(array: T[]): T[] {
  return Array.from(new Set(array));
}

export function getStatsFilters(selectedCampaigns: Record<string, any>[]) {
  let allFilters = [];
  const atLeastOneDoohCampaign = !!selectedCampaigns.find(campaign => {
    return isDOOHCampaign(campaign.type);
  });
  const atLeastOneStandardCampaign = !!selectedCampaigns.find(campaign => {
    return isDigitalCampaign(campaign.type);
  });
  if (atLeastOneStandardCampaign) {
    allFilters.push(...statsFilters.stats.standard);
  }
  if (atLeastOneDoohCampaign) {
    allFilters.push(...statsFilters.stats.dooh);
    allFilters = getUniqueKeys(allFilters);
  }
  return allFilters;
}

function includeViewabilityProviders(
  user: Record<string, any>,
  userRoles: UserRoleInterface[],
  selectedCampaigns: Record<string, any>[]
) {
  const campaignsWithViewabilityProviders = selectedCampaigns.filter(campaign => {
    return campaign.hasViewabilityProviders;
  });
  return (
    campaignsWithViewabilityProviders.length === selectedCampaigns.length &&
    checkUserResourcePermissions('reportingIasViewabilityEvents', user, userRoles)
  );
}

function includeViewabilityStats(
  user: Record<string, any>,
  userRoles: UserRoleInterface[],
  selectedCampaigns: Record<string, any>[]
) {
  return (
    selectedCampaigns.filter(campaign => campaign.hasViewability).length > 0 &&
    checkUserResourcePermissions('reportingIasViewabilityEvents', user, userRoles)
  );
}

function filterUserStatsFilters(
  type: TableType,
  user: Record<string, any>,
  userRoles: UserRoleInterface[],
  selectedCampaigns: Record<string, any>[],
  cartographyLevels: GeoDictionaryTagsConfig[],
  variables: string[],
  isExport = false
) {
  const digitalConversionFilters = ['visits', 'sections', 'finals', 'provenance', 'permanence'];
  const footfallAttributionFilters = ['attrProviders'];
  const filteredFilters = cloneDeep(variables).filter(variable => {
    if (variable === 'cid' && !isExport && selectedCampaigns.length === 1) {
      return false;
    }
    if (['footfallAttribution', 'stats'].includes(type) && digitalConversionFilters.includes(variable)) {
      return (
        checkUserResourcePermissions('reportingDigitalConversions', user, userRoles) &&
        campaignsHaveDigitalConversions(selectedCampaigns)
      );
    }
    if (type === 'viewability' && variable.indexOf('Sc') > -1) {
      return checkUserResourcePermissions('reportingIasViewabilityBrandSafetyCategory', user, userRoles);
    }
    if (type === 'viewability' && (variable.indexOf('AdFraud') > -1 || variable.indexOf('SadScore') > -1)) {
      return checkUserResourcePermissions('reportingIasViewabilityFraudCategory', user, userRoles);
    }
    if (type === 'stats' && ['visits', 'sections', 'finals'].includes(variable)) {
      return checkUserResourcePermissions('reportingFilterDigitalConversionSource', user, userRoles);
    }
    if (variable === 'viewabilityProviders') {
      return includeViewabilityProviders(user, userRoles, selectedCampaigns);
    }
    if (variable.indexOf('viewability') !== -1) {
      return includeViewabilityStats(user, userRoles, selectedCampaigns);
    }
    if (type === 'stats' && variable.indexOf('device') > -1) {
      if (selectedCampaigns.filter(c => isDigitalCampaign(c.type)).length === 0) {
        return false;
      }
    }
    if (type === 'stats' && (variable === 'lid' || variable === 'lgid')) {
      if (selectedCampaigns.filter(c => isDigitalCampaign(c.type) || isDOOHCampaign(c.type)).length === 0) {
        return false;
      }
    }
    if (type === 'stats' && (variable.includes('screen') || variable.includes('cartography'))) {
      if (selectedCampaigns.filter(c => isDOOHCampaign(c.type)).length === 0) {
        return false;
      }
    }
    if (type === 'stats' && variable === 'dayOfWeekToggleId') {
      if (selectedCampaigns.filter(c => c.temporalPacing.dayOfWeekEnabled).length === 0) {
        return false;
      }
    }
    if (type === 'stats' && variable === 'dayOfYearToggleId') {
      if (selectedCampaigns.filter(c => c.temporalPacing.dayOfYearEnabled).length === 0) {
        return false;
      }
    }
    if (['footfallAttribution', 'stats'].includes(type) && variable === 'thirdPartyCategoriesIas') {
      return (
        selectedCampaigns.some(campaign => campaign.contextualTargeting?.iasCategories?.enabled) &&
        checkUserResourcePermissions('reportingFilterContentCategoriesIAS', user, userRoles)
      );
    }
    if (['footfallAttribution', 'stats'].includes(type) && footfallAttributionFilters.includes(variable)) {
      return (
        selectedCampaigns.filter(campaign => campaign.hasNewAttributionStats).length > 0 &&
        (checkUserResourcePermissions('reportingFootfallAttribution', user, userRoles) ||
          checkUserResourcePermissions('reportingTotalFootfallAttribution', user, userRoles))
      );
    }
    const filterPermission = `reportingFilter${variable.substring(0, 1).toUpperCase()}${variable.substring(1)}`;
    if (
      type === 'stats' &&
      variable === 'contextualBiddingScenarioId' &&
      checkUserResourcePermissions(filterPermission, user, userRoles) &&
      !campaignsHaveContextualBidding(selectedCampaigns)
    ) {
      return false;
    }
    if (
      type === 'stats' &&
      variable === 'dcoContextId' &&
      checkUserResourcePermissions(filterPermission, user, userRoles) &&
      !campaignsHaveDCO(selectedCampaigns)
    ) {
      return false;
    }
    if (
      type === 'stats' &&
      variable === 'dcoScenarioId' &&
      checkUserResourcePermissions(filterPermission, user, userRoles) &&
      !campaignsHaveDCO(selectedCampaigns)
    ) {
      return false;
    }
    return checkUserResourcePermissions(filterPermission, user, userRoles);
  });
  if (type === 'stats') {
    const cartographyFilters = cartographyLevels
      .filter(() => checkUserResourcePermissions('reportingFilterCartographyLevels', user, userRoles))
      .map(tag => `cartoPyramid.${tag.id}`);
    let cartographyFiltersPosition = filteredFilters.indexOf('lgid') + 1;
    if (selectedCampaigns.filter(c => isDOOHCampaign(c.type)).length > 0) {
      cartographyFiltersPosition = filteredFilters.indexOf('screenVenueTypes') + 1;
    }
    filteredFilters.splice(cartographyFiltersPosition, 0, ...cartographyFilters);
  }
  return filteredFilters;
}

export function getStatsTableVariableFilters(
  type: TableType,
  user: Record<string, any>,
  userRoles: UserRoleInterface[],
  selectedCampaigns: Record<string, any>[],
  cartographyLevels: GeoDictionaryTagsConfig[] = [],
  isExport = false
) {
  const variables = (
    type === 'footfallAttribution'
      ? statsFilters.footfallAttribution
      : type === 'viewability'
        ? statsFilters.viewability
        : getStatsFilters(selectedCampaigns)
  ).filter(variable => checkUserResourcePermissions(getVariableFilterPermission(variable), user, userRoles));
  return filterUserStatsFilters(type, user, userRoles, selectedCampaigns, cartographyLevels, variables, isExport);
}

export function getTableOutputMetrics(type: TableType, variables: string[], dataFees: string[] = []): string[] {
  return getUniqueKeys([...variables, ...dataFees, ...COMMON_TABLE_VARIABLES, ...TABLE_VARIABLES_BY_TYPE[type]]);
}

export const StatsColumnsDictionary: { [key: string]: string } = {
  avg_store_visits: 'avg_footfall_attribution',
  d2s_baseline: 'fa_venue_baseline',
  d2s_income: 'fa_venue_income',
  d2s_roi: 'fa_venue_roi',
  d2s_store_visits: 'fa_venue_uplift',
  d2s_total_spent: 'fa_venue_total_spent',
  d2s_uplift_cost: 'fa_venue_cost_per_visit',
  d2s_uplift_rate: 'fa_venue_uplift_pct',
  store_visits: 'footfall_attribution',
  store_visits_hawking: 'footfall_attribution_hawking'
};

export const DISABLED_METRICS = [
  'action',
  'cpa',
  'avg_proximity_visits',
  'avg_unique_proximity_visits',
  'avg_footfall_attribution',
  'avg_footfall_attribution_preprocessed',
  'avg_unique_store_visits',
  'avg_store_visits',
  'avg_acquisition_rate'
];
